// Used in:
// - src/new/utils/resourceTabs/index.tsx (resource detail tabs)
// - src/new/components/monitor/ResourceTriage/ResourceTriage.tsx
import React, { useEffect, useState } from 'react';
import styles from './Stats.module.scss';
import { Stack } from 'new/elements/Stack';
import { Typography } from '@mui/material';
import { useResourceUsageMetrics } from 'new/hooks/adapters/useResourceUsageMetrics';
import { RESOURCE_TYPE_DATA_SET } from 'js/constants/resourceTypes';
import { useDispatch, useSelector } from 'react-redux';
import { fetchDataSetUpdateTime, fetchResourceMetricsAggregate } from 'js/actions';
import DateUtils from 'js/lib/DateUtils';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import { GET_RESOURCE_METRICS_AGGREGATE_REQUEST } from 'js/constants/actionTypes';
import { shouldHideRecordsForResource } from 'js/lib/PipelineUtils';
import {
  getPresetDateRange,
  MetricsDateRange,
} from '@modules/monitor/resource-triage/components/MetricsTrendPanel';
import { MetricsPanel } from '@modules/monitor/resource-triage/components/MetricsPanel';
import { MetricsUtil } from 'new/utils/MetricsUtil';

interface NexsetStatsProps {
  resourceId?: string;
  data?: { id: string; flow_type?: string };
  resourceType: string;
}

export const Stats = (props: NexsetStatsProps) => {
  const { resourceType, data: resourceData } = props;
  let { resourceId } = props;
  if (!resourceId) {
    resourceId = resourceData.id;
  }
  const dispatch = useDispatch();
  const loading = useIsLoading(GET_RESOURCE_METRICS_AGGREGATE_REQUEST);
  const sampleUpdateTime = useSelector(
    (state: any) => state.dataSets?.sampleUpdateTimeById[resourceId],
  );
  const { data, fetch, usage } = useResourceUsageMetrics(resourceId, resourceType);

  // check if we hide records for this flow type
  const hideRecords = shouldHideRecordsForResource(resourceData);

  const [metricsRange, setMetricsRange] = useState(() => getPresetDateRange('30d'));

  const onRefresh = () => {
    if (resourceType === RESOURCE_TYPE_DATA_SET) {
      dispatch(fetchDataSetUpdateTime(resourceId) as any);
    }
    dispatch(fetchResourceMetricsAggregate(resourceId, resourceType) as any);
    fetch(metricsRange);
  };

  useEffect(() => {
    onRefresh();
  }, [resourceType, resourceId]);

  const onDatesChanged = (dates: MetricsDateRange) => {
    setMetricsRange({ start: new Date(dates.start), end: new Date(dates.end) });
    fetch(dates);
  };

  return (
    <Stack>
      <Stack className={styles.usageContainer}>
        {resourceType === RESOURCE_TYPE_DATA_SET ? (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            className={styles.usageTop}
          >
            <div />
            <Stack direction="row" gap="8px" alignItems="center">
              <Typography variant="body3">
                {DateUtils.convertDateStringToFormat(sampleUpdateTime)}
              </Typography>
            </Stack>
          </Stack>
        ) : null}

        <MetricsPanel
          lifetimeMetrics={MetricsUtil.handleLifetimeMetrics(usage)}
          trendData={data}
          start={metricsRange.start}
          end={metricsRange.end}
          onDatesChanged={onDatesChanged}
          onRefresh={onRefresh}
          hideRecords={hideRecords}
          loading={loading}
          title="Data record"
          resourceType={resourceType}
          resourceId={resourceId}
        />
      </Stack>
    </Stack>
  );
};
